import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import useEvent from "react-use-event-hook";
import { useRecoilState, useRecoilValue, useSetRecoilState } from "recoil";

import { useCrudEditContext } from "@ryde/redux";
import {
  ComputedDataKey,
  createRouteRebuildAppliedAt,
  DirectionsServiceEnum,
  editFormDataAF,
  EditFormMode,
  formatRouteRebuildSuccessToast,
  IRebuildRouteDefaultActionOptions,
  isCustomRebuildRouteType,
  isDefaultActionRebuildRouteType,
  IStationRouteComputedData,
  IStationRouteDto,
  lastRouteRebuildAtAtom,
  lastSelectedRebuildTypeAtom,
  Log,
  normalizeStationRouteComputedData,
  PROVIDERS_WITH_AVOID_TOLLS,
  rebuildRouteLoadingAtom,
  RebuildRouteTypeEnum,
  RecountStationRouteDistancesCriteriaDto,
  resolveInitialRebuildRouteProvider,
  resolveRebuildRouteProvider,
  resolveSkipCacheDefaultRebuildType,
  resolveStationRouteDirectionsService,
  SimpleResult,
  StationRouteApi,
  toDisplayedRebuildRouteType,
  useSystemParams,
  useTimezone,
  warnAndToastSimpleResult,
  withStationRouteRoutingEngineMetadata,
} from "@ryde/shared";

export const useStationRouteRebuildRouteDropdown = (
  stationRouteDto: Partial<IStationRouteDto> | undefined,
) => {
  const { t } = useTranslation();
  const { timezone } = useTimezone();
  const { entity, mode } = useCrudEditContext<IStationRouteDto>();
  const [, setStationRouteDtoGlobal] = useRecoilState<
    Partial<IStationRouteDto> | undefined
  >(editFormDataAF([entity, mode || EditFormMode.CrudRoute]));
  const systemParams = useSystemParams();

  const [open, setOpen] = useState(false);
  const [isLoading, setIsLoading] = useRecoilState(rebuildRouteLoadingAtom);
  const lastSelectedRebuildType = useRecoilValue(lastSelectedRebuildTypeAtom);
  const lastRouteRebuildAt = useRecoilValue(lastRouteRebuildAtAtom);
  const setLastSelectedRebuildType = useSetRecoilState(
    lastSelectedRebuildTypeAtom,
  );
  const setLastRouteRebuildAt = useSetRecoilState(lastRouteRebuildAtAtom);

  const systemDefaultProvider =
    systemParams.directionsService || DirectionsServiceEnum.Google;
  const lastUsedProvider = resolveStationRouteDirectionsService(
    stationRouteDto?.metadata,
  );

  const [selectedProvider, setSelectedProvider] =
    useState<DirectionsServiceEnum | null>(() =>
      resolveInitialRebuildRouteProvider({
        lastUsedProvider,
        systemDefaultProvider,
      }),
    );
  const [avoidTolls, setAvoidTolls] = useState<boolean>(
    systemParams.avoidTolls ?? false,
  );
  const [skipCache, setSkipCache] = useState(true);
  const [routeOption, setRouteOption] = useState<RebuildRouteTypeEnum>(
    RebuildRouteTypeEnum.ProviderOptimization,
  );
  const [providerTouched, setProviderTouched] = useState(false);
  const setSelectedProviderWithTouch = (
    provider: DirectionsServiceEnum,
  ): void => {
    setProviderTouched(true);
    setSelectedProvider(provider);
  };

  useEffect(() => {
    setProviderTouched(false);
  }, [stationRouteDto?.id]);

  useEffect(() => {
    if (providerTouched) {
      return;
    }
    setSelectedProvider(
      resolveInitialRebuildRouteProvider({
        lastUsedProvider,
        systemDefaultProvider,
      }),
    );
  }, [
    lastUsedProvider,
    systemDefaultProvider,
    providerTouched,
    stationRouteDto?.id,
  ]);

  const handleDropdownToggle = useEvent((event: React.MouseEvent) => {
    event.stopPropagation();
    setOpen(prevOpen => !prevOpen);
  });

  const handleClose = useEvent((_event: Event | React.SyntheticEvent) => {
    setOpen(false);
  });

  const supportsAvoidTolls = selectedProvider
    ? PROVIDERS_WITH_AVOID_TOLLS.includes(selectedProvider)
    : true;

  const resolveDefaultRebuildType = (): RebuildRouteTypeEnum =>
    resolveSkipCacheDefaultRebuildType({ skipCachedPaths: skipCache });

  const handleDefaultRebuild = useEvent(
    async (
      rebuildType?: RebuildRouteTypeEnum,
      options?: IRebuildRouteDefaultActionOptions,
    ) => {
      const stations = stationRouteDto?.computedData?.stationInfos || [];

      if (!stations?.length) {
        Log.warnAndToast(t("errors.noRouteToRebuild"));
        return;
      }

      if (isLoading) {
        return;
      }

      setIsLoading(true);
      setOpen(false);

      try {
        const provider =
          options?.provider ??
          resolveInitialRebuildRouteProvider({
            lastUsedProvider,
            systemDefaultProvider,
          });

        const criteriaRecount = new RecountStationRouteDistancesCriteriaDto({
          stations,
          optimize: true,
          provider,
          rebuildType: isDefaultActionRebuildRouteType(rebuildType)
            ? rebuildType
            : resolveDefaultRebuildType(),
        });

        const recounted =
          await StationRouteApi.recountStationInfo(criteriaRecount);

        if (!recounted?.success || !recounted.data?.stationsInfo) {
          warnAndToastSimpleResult(
            new SimpleResult({
              success: false,
              error: t("errors.stationRouteNotRecountedSuccessfully"),
            }),
            t,
          );
          return;
        }

        const currentComputedData: IStationRouteComputedData = {
          ...(stationRouteDto?.computedData || {}),
        };
        const { stationsInfo, result: routeResult } = recounted.data;
        currentComputedData.stationInfos = stationsInfo;

        const computedDataRecounted = normalizeStationRouteComputedData(
          currentComputedData,
          false,
          timezone,
        );

        const routingEngine =
          recounted.data.directionsService ??
          recounted.data.provider ??
          provider;
        const routeRebuildAt = createRouteRebuildAppliedAt();
        setLastRouteRebuildAt(routeRebuildAt);

        setStationRouteDtoGlobal(dto => ({
          ...dto,
          result: routeResult,
          [ComputedDataKey]: computedDataRecounted,
          ...(routingEngine
            ? {
                metadata: withStationRouteRoutingEngineMetadata({
                  metadata: dto?.metadata,
                  engine: routingEngine,
                  routeRebuildAt,
                }),
              }
            : {}),
        }));

        Log.infoAndToast(
          formatRouteRebuildSuccessToast({
            baseMessage: t("stationRoute.ROUTE_REBUILD_SUCCESSFULLY"),
            provider: routingEngine,
            routeRebuildSource: recounted.data.routeRebuildSource,
            t,
          }),
        );
        setLastSelectedRebuildType(
          toDisplayedRebuildRouteType(
            isDefaultActionRebuildRouteType(rebuildType)
              ? rebuildType
              : resolveDefaultRebuildType(),
          ),
        );
      } finally {
        setIsLoading(false);
      }
    },
  );

  const handleRebuild = useEvent(async () => {
    const stations = stationRouteDto?.computedData?.stationInfos || [];

    if (!stations?.length) {
      Log.warnAndToast(t("errors.noRouteToRebuild"));
      return;
    }

    if (isLoading) {
      return;
    }

    const rebuildType =
      routeOption || RebuildRouteTypeEnum.ProviderOptimization;

    setIsLoading(true);

    try {
      const provider = resolveRebuildRouteProvider({
        selectedProvider,
        lastUsedProvider,
        systemDefaultProvider,
      });

      // For station routes: ProviderOptimization and OptimizeStationOrderOsrmFirst always use optimize=true
      // (no exceptions, unlike rides)
      const optimize =
        rebuildType === RebuildRouteTypeEnum.ProviderOptimization ||
        rebuildType === RebuildRouteTypeEnum.OptimizeStationOrderOsrmFirst ||
        rebuildType === RebuildRouteTypeEnum.EndpointsLocked;

      const criteriaRecount = new RecountStationRouteDistancesCriteriaDto({
        stations,
        optimize,
        avoidTolls,
        provider,
        rebuildType,
      });

      const recounted =
        await StationRouteApi.recountStationInfo(criteriaRecount);

      if (!recounted?.success || !recounted.data?.stationsInfo) {
        warnAndToastSimpleResult(
          new SimpleResult({
            success: false,
            error: t("errors.stationRouteNotRecountedSuccessfully"),
          }),
          t,
        );
        return;
      }

      const currentComputedData: IStationRouteComputedData = {
        ...(stationRouteDto?.computedData || {}),
      };
      const { stationsInfo, result: routeResult } = recounted.data;
      currentComputedData.stationInfos = stationsInfo;

      const computedDataRecounted = normalizeStationRouteComputedData(
        currentComputedData,
        false,
        timezone,
      );

      const routingEngine =
        recounted.data.directionsService ?? recounted.data.provider ?? provider;
      const routeRebuildAt = createRouteRebuildAppliedAt();
      setLastRouteRebuildAt(routeRebuildAt);

      setStationRouteDtoGlobal(dto => ({
        ...dto,
        result: routeResult,
        [ComputedDataKey]: computedDataRecounted,
        ...(routingEngine
          ? {
              metadata: withStationRouteRoutingEngineMetadata({
                metadata: dto?.metadata,
                engine: routingEngine,
                routeRebuildAt,
              }),
            }
          : {}),
      }));

      Log.infoAndToast(
        formatRouteRebuildSuccessToast({
          baseMessage: t("stationRoute.ROUTE_REBUILD_SUCCESSFULLY"),
          provider: routingEngine,
          routeRebuildSource: recounted.data.routeRebuildSource,
          t,
        }),
      );
      setLastSelectedRebuildType(toDisplayedRebuildRouteType(rebuildType));
    } finally {
      setIsLoading(false);
      setOpen(false);
    }
  });

  const hasCustomRebuildType = isCustomRebuildRouteType(
    lastSelectedRebuildType,
  );

  return {
    open,
    isLoading,
    systemDefaultProvider,
    lastUsedProvider,
    selectedProvider,
    setSelectedProvider: setSelectedProviderWithTouch,
    avoidTolls,
    setAvoidTolls,
    skipCache,
    setSkipCache,
    routeOption,
    setRouteOption,
    supportsAvoidTolls,
    systemDefaultAvoidTolls: systemParams.avoidTolls ?? false,
    hasCustomRebuildType,
    lastSelectedRebuildType,
    lastRouteRebuildAt,
    handleDropdownToggle,
    handleClose,
    handleDefaultRebuild,
    handleRebuild,
  };
};
